/**
 * WizardStepList
 *
 * Displays the list of wizard steps with their statuses.
 * Shows completed, current, and pending steps.
 */

import React from 'react';
import { cn } from '@/lib/utils';
import { Check, Circle, Loader2, XCircle } from 'lucide-react';

export interface WizardStep {
  key: string;
  label: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'skipped';
  order: number;
}

interface WizardStepListProps {
  steps: WizardStep[];
  currentStep: string;
  onStepClick?: (stepKey: string) => void;
}

export function WizardStepList({ steps, currentStep, onStepClick }: WizardStepListProps) {
  return (
    <div className="space-y-1">
      <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide mb-3">
        Steps
      </h3>
      <nav className="space-y-1">
        {steps.map((step) => {
          const isCurrent = step.key === currentStep;
          const isClickable = step.status === 'completed' || step.status === 'failed';

          return (
            <button
              key={step.key}
              onClick={() => isClickable && onStepClick?.(step.key)}
              disabled={!isClickable}
              className={cn(
                'w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors text-left',
                isCurrent && 'bg-primary/10 text-primary font-medium',
                !isCurrent && step.status === 'completed' && 'text-muted-foreground hover:bg-muted cursor-pointer',
                !isCurrent && step.status === 'pending' && 'text-muted-foreground/50 cursor-not-allowed',
                !isCurrent && step.status === 'failed' && 'text-destructive hover:bg-destructive/10 cursor-pointer',
                !isCurrent && step.status === 'running' && 'text-primary'
              )}
            >
              <StepIcon status={step.status} isCurrent={isCurrent} />
              <span className="truncate">{step.label}</span>
            </button>
          );
        })}
      </nav>
    </div>
  );
}

function StepIcon({ status, isCurrent }: { status: WizardStep['status']; isCurrent: boolean }) {
  if (status === 'completed') {
    return <Check className="h-4 w-4 text-green-500 shrink-0" />;
  }
  if (status === 'failed') {
    return <XCircle className="h-4 w-4 text-destructive shrink-0" />;
  }
  if (status === 'running' || isCurrent) {
    return <Loader2 className="h-4 w-4 animate-spin text-primary shrink-0" />;
  }
  return <Circle className="h-4 w-4 text-muted-foreground/30 shrink-0" />;
}
